/* global PACKAGE_VERSION */
import { initTelemetry } from 'pf-core/services/telemetry';
import { createRoot } from 'react-dom/client';
import 'pf-core/index.css';
import 'pf-core/schedulerpro.css';
import React, { Suspense } from 'react';
import { areCookiesAvailable } from './utilities/checkCookie';
import { RedirectToLogin } from './features/Redirect/index';
import { Loader } from './assets/Loader/Loader';
import { PreSignedFileDownload } from 'features/PreSignedFileDownload';
import { DOCUMENT_DOWNLOAD_ROUTE } from 'features/PreSignedFileDownload/constants';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
  ArcElement,
  Filler,
  RadialLinearScale,
  Title,
  Tooltip,
  Legend,
  DoughnutController
} from 'chart.js';
import { MatrixController, MatrixElement } from 'chartjs-chart-matrix';

// Kill switch: no environment has the Alloy/Faro receiver deployed yet, so
// this fires unconditionally against a backend with no /faro route,
// surfacing as CORS/404 errors in the browser. Off by default until infra
// confirms an environment is ready; flip REACT_APP_FARO_ENABLED to 'true'
// per-environment once it is. (pf-core's own kill switch doesn't reach this
// app until pf-core is republished and this app bumps -- see pf-core PR.)
if (process.env.REACT_APP_FARO_ENABLED === 'true') {
  initTelemetry({
    appName: 'user',
    appVersion: typeof PACKAGE_VERSION === 'undefined' ? 'dev' : PACKAGE_VERSION,
    buildId: process.env.REACT_APP_BUILD_ID || 'local-dev'
  });
}

// Custom SunburstController for Sunburst charts
class SunburstController extends DoughnutController {
  draw () {
    super.draw();

    const { ctx, chartArea, data } = this.chart;
    const meta = this.getMeta();
    const total = data.datasets[0].data.reduce((a, b) => a + b, 0);

    ctx.save();
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillStyle = (
      getComputedStyle(document.documentElement)
        .getPropertyValue('--fg-primary')
        .trim() || '#202020'
    );
    ctx.font = '10px \'Open Sans\'';

    meta.data.forEach((arc, idx) => {
      const { startAngle, endAngle, innerRadius, outerRadius } = arc;
      const midAngle = (startAngle + endAngle) / 2;
      const radius = (innerRadius + outerRadius) / 2;

      const x =
        (chartArea.left + chartArea.right) / 2 + Math.cos(midAngle) * radius;
      const y =
        (chartArea.top + chartArea.bottom) / 2 + Math.sin(midAngle) * radius;

      const val = data.datasets[0].data[idx];
      if (!val) {
        return;
      }

      const pct = ((val / total) * 100).toFixed(2) + '%';

      if (endAngle - startAngle < 0.2) {
        return;
      }

      ctx.fillText(pct, x, y);
    });

    ctx.restore();
  }
}
SunburstController.id = 'sunburst';
SunburstController.defaults = DoughnutController.defaults;

// Register all Chart.js components globally
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
  ArcElement,
  Filler,
  RadialLinearScale,
  Title,
  Tooltip,
  Legend,
  MatrixController,
  MatrixElement,
  SunburstController
);
const UserApplication = React.lazy(() => import('./features/App'));

function App () {
  const location = window.location;
  if (location.pathname.includes(DOCUMENT_DOWNLOAD_ROUTE)) {
    return <PreSignedFileDownload />;
  }
  if (!areCookiesAvailable()) {
    return <RedirectToLogin />;
  }
  return (
    <Suspense fallback={<Loader type="fullScreen" />}>
      <UserApplication />
    </Suspense>
  );
}

const container = document.getElementById('root');
const root = createRoot(container);
root.render(<App />);
